import { READ_METHOD_WORDS, WRITE_METHOD_WORDS } from "./appWords.js";
import { originHost, webOrigin } from "./webOrigin.js";
/** The four sensitivity levels the workbench, the node and AIOE share (shared/schemas/sensitivityLabels.ts, kept zod-free here). */
export const APP_LABELS = ["public", "internal", "confidential", "restricted"];
const RANK = { public: 0, internal: 1, confidential: 2, restricted: 3 };
export const labelRank = (l) => RANK[l];
export const isAppLabel = (v) => typeof v === "string" && v in RANK;
export const higherLabel = (a, b) => (b && RANK[b] > RANK[a] ? b : a);
/** "Confidential". */
export const labelWord = (l) => l.charAt(0).toUpperCase() + l.slice(1);
/** Reads that say how the workbench is doing rather than what the project holds: Internal whatever the project's classification. */
const METADATA_READS = new Set(["workbench.status", "usage.history", "teams.list"]);
/** Writes that land on the task board (or the roadmap beside it), so leave the Workbench when the board mirrors to an outside tracker. */
const BOARD_WRITES = new Set(["board.create", "board.update", "board.move", "board.assign", "board.comment", "roadmap.upsert"]);
/** The statement of an app as it stands in this project. */
/** The other repositories a manifest's pipelines reads name (D390), once each, in order. */
export function outsideRepositories(bindings) {
    const out = [];
    for (const b of bindings) {
        const list = b.method === "pipelines.list" ? b.params?.repositories : undefined;
        if (Array.isArray(list))
            for (const r of list)
                if (typeof r === "string" && !out.includes(r))
                    out.push(r);
    }
    return out;
}
/** A read's words, with the other repositories it reaches: "pipelines and their runs, here and in Mojo-Up/mju-aioe". */
function readWordsOf(method, repositories) {
    const words = READ_METHOD_WORDS[method] ?? method;
    if (!repositories.length)
        return words;
    const names = repositories.length > 3 ? [...repositories.slice(0, 3), `${repositories.length - 3} more`] : [...repositories];
    const list = names.length === 1 ? names[0] : `${names.slice(0, -1).join(", ")} and ${names[names.length - 1]}`;
    return `${words}, here and in ${list}`.slice(0, 200);
}
export function deriveStatement(m, ctx = {}) {
    const content = ctx.classification ?? "internal";
    const reads = [];
    for (const b of m.bindings) {
        if (reads.some((r) => r.method === b.method))
            continue;
        const repositories = outsideRepositories(m.bindings.filter((x) => x.method === b.method));
        reads.push({ method: b.method, words: readWordsOf(b.method, repositories), label: METADATA_READS.has(b.method) ? "internal" : content });
    }
    const external = [];
    for (const t of m.toolReads ?? []) {
        if (external.some((s) => s.kind === "connector" && s.server === t.server && s.tool === t.tool))
            continue;
        external.push({ kind: "connector", server: t.server, tool: t.tool, words: `${t.tool} through the ${t.server} connector` });
    }
    for (const w of m.webReads ?? []) {
        const origin = webOrigin(w.url);
        const secret = w.auth?.secret;
        if (!origin || external.some((s) => s.kind === "web" && s.origin === origin && s.secret === secret))
            continue;
        external.push({ kind: "web", origin, words: `web data from ${originHost(origin)}`, ...(secret ? { secret } : {}) });
    }
    const actions = m.actions.map((a) => ({
        name: a.name,
        method: a.method,
        words: WRITE_METHOD_WORDS[a.method] ?? a.method,
        ...(ctx.boardMirror && BOARD_WRITES.has(a.method) ? { sendsOut: ctx.boardMirror } : {}),
    }));
    let label = "public";
    for (const r of reads)
        label = higherLabel(label, r.label);
    if (isAppLabel(m.label))
        label = higherLabel(label, m.label);
    return { v: 1, reads, external, actions, label, sendsOut: actions.some((a) => !!a.sendsOut), combines: labelRank(label) > RANK.internal && external.length > 0 };
}
/** A source's or an egress action's identity, for comparing two statements. */
const sourceKey = (s) => (s.kind === "connector" ? `connector:${s.server}/${s.tool}` : `web:${s.origin}`);
const egressKey = (a) => `egress:${a.method}>${a.sendsOut ?? ""}`;
/** A key used with an address (D390): its name and where it goes. */
const keyUses = (s) => s.external.flatMap((x) => (x.kind === "web" && x.secret ? [{ key: `${x.secret}>${x.origin}`, line: keyLine(x.secret, x.origin) }] : []));
/** "Uses your EXPO_TOKEN key with api.expo.dev": the name of the key and where it goes, never its value. */
export const keyLine = (secret, origin) => `Uses your ${secret} key with ${originHost(origin)}`;
/** One external source in the statement's words: "Reads list_issues through the github connector (external)". */
export const sourceLine = (s) => `Reads ${s.words} (external)`;
/** One action: "Can add a task", or "Can add a task, which sends it to GitHub Issues". */
export const actionLine = (a) => (a.sendsOut ? `Can ${a.words}, which sends it to ${a.sendsOut}` : `Can ${a.words}`);
/** The statement as the short list a person reads, in order: reads, external sources, actions, the mix, what it sends out. */
export function statementLines(s) {
    const out = [];
    const seen = new Set();
    for (const r of s.reads) {
        const line = `Reads ${r.words} (${labelWord(r.label)})`;
        if (!seen.has(line))
            out.push(line);
        seen.add(line);
    }
    for (const x of s.external)
        if (!out.includes(sourceLine(x)))
            out.push(sourceLine(x));
    for (const k of keyUses(s))
        if (!out.includes(k.line))
            out.push(k.line);
    const doing = [...new Set(s.actions.map(actionLine))];
    out.push(...doing);
    if (s.combines)
        out.push(`Puts ${labelWord(s.label)} data beside data from outside the Workbench`);
    if (!s.reads.length && !s.external.length)
        out.push("Reads nothing");
    const out2 = [...new Set(s.actions.filter((a) => a.sendsOut).map((a) => a.sendsOut))];
    out.push(out2.length ? `Sends data out to ${out2.join(" and ")} only when you use it` : "Sends nothing out");
    return out;
}
/** The names of an app's external sources, for the External data chip's hint: "the github connector", "api.example.com". */
export function externalNames(s) {
    return [...new Set(s.external.map((x) => (x.kind === "connector" ? `the ${x.server} connector` : originHost(x.origin))))];
}
/**
 * What a statement adds over one already consented to, in a person's words, or nothing when it is narrower or equal: a new
 * external source, a new action that sends data out, a higher label. A new Control API read at or below the consented label,
 * or a new action that stays inside, is not a widening (the policy's scope ceiling already governs those).
 */
export function statementWidens(before, after) {
    const out = [];
    const had = new Set(before.external.map(sourceKey));
    for (const s of after.external)
        if (!had.has(sourceKey(s)))
            out.push(`Now ${sourceLine(s).charAt(0).toLowerCase()}${sourceLine(s).slice(1)}`);
    // A key put to a new use asks again (D390), whether its address is new or not.
    const keys = new Set(keyUses(before).map((k) => k.key));
    for (const k of keyUses(after))
        if (!keys.has(k.key))
            out.push(`Now ${k.line.charAt(0).toLowerCase()}${k.line.slice(1)}`);
    const sent = new Set(before.actions.filter((a) => a.sendsOut).map(egressKey));
    for (const a of after.actions)
        if (a.sendsOut && !sent.has(egressKey(a)))
            out.push(`Now can ${a.words}, which sends it to ${a.sendsOut}`);
    if (labelRank(after.label) > labelRank(before.label))
        out.push(`Now reads ${labelWord(after.label)} data (it read ${labelWord(before.label)} before)`);
    return [...new Set(out)];
}
/**
 * The statement an app with no consent on record is held to: Workbench reads up to Internal, nothing external, nothing sent out.
 * An app within it runs without asking (what it reads is what the policy's scope ceiling already allows); one beyond it shows
 * the consent card once.
 */
export const BASELINE_STATEMENT = { v: 1, reads: [], external: [], actions: [], label: "internal", sendsOut: false, combines: false };
/** The statement in a stable text: what a consent record's digest is taken of. */
export function canonicalStatement(s) {
    const sort = (v) => {
        if (Array.isArray(v))
            return v.map(sort);
        if (v && typeof v === "object")
            return Object.fromEntries(Object.entries(v).sort(([a], [b]) => (a < b ? -1 : a > b ? 1 : 0)).map(([k, x]) => [k, sort(x)]));
        return v;
    };
    return JSON.stringify(sort(s));
}
//# sourceMappingURL=appStatement.js.map